import { useState, type ReactNode } from 'react'
import { ArrowUpLeft, Chrome, LoaderCircle, ShieldCheck, Sparkles } from 'lucide-react'
import { BrandMark } from './BrandMark'
import { firebaseEnabled, useAuthSession } from '../../lib/firebase'

interface AuthGateProps {
  children: ReactNode
}

export function AuthGate({ children }: AuthGateProps) {
  const session = useAuthSession()
  const [error, setError] = useState<string | null>(null)
  const [signingIn, setSigningIn] = useState(false)

  if (session.loading) {
    return (
      <main className="auth-loading" aria-live="polite">
        <LoaderCircle className="spin" size={24} aria-hidden="true" />
        <span>جارٍ تجهيز المنصة…</span>
      </main>
    )
  }

  if (session.accountError) {
    return (
      <main className="auth-loading auth-loading--blocked" role="alert">
        <ShieldCheck size={28} aria-hidden="true" />
        <strong>{session.accountError}</strong>
        <button className="google-button" type="button" onClick={() => void session.signOut()}>تسجيل الخروج</button>
      </main>
    )
  }

  if (session.user || !firebaseEnabled) return <>{children}</>

  async function handleSignIn() {
    setError(null)
    setSigningIn(true)
    try {
      await session.signIn()
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'حدث خطأ أثناء تسجيل الدخول.'
      setError(message)
    } finally {
      setSigningIn(false)
    }
  }

  return (
    <main className="auth-screen">
      <div className="auth-screen__backdrop" aria-hidden="true">
        <span className="backdrop-ring backdrop-ring--one" />
        <span className="backdrop-ring backdrop-ring--two" />
        <span className="backdrop-stamp">OSPE</span>
      </div>
      <section className="auth-card" aria-labelledby="auth-title">
        <BrandMark />
        <div className="auth-card__eyebrow">
          <span className="eyebrow-line" />
          مراجعة عملي الجراحة
        </div>
        <h1 id="auth-title">راجع محطات العملي،<br /><em>بسهولة وتركيز.</em></h1>
        <p className="auth-card__intro">
          كل الصور والسيناريوهات والأسئلة في مكان واحد يساعدك على الاستعداد للامتحان.
        </p>
        <button className="google-button" type="button" onClick={handleSignIn} disabled={signingIn}>
          {signingIn ? <LoaderCircle className="spin" size={19} /> : <Chrome size={19} aria-hidden="true" />}
          <span>{signingIn ? 'جارٍ تسجيل الدخول…' : 'تسجيل الدخول باستخدام Google'}</span>
          <ArrowUpLeft size={18} className="google-button__arrow" aria-hidden="true" />
        </button>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="auth-card__note">
          <ShieldCheck size={15} aria-hidden="true" />
          <span>استخدم حساب Google المصرّح له للدخول إلى المنصة.</span>
        </div>
        <div className="auth-card__footer">
          <Sparkles size={14} aria-hidden="true" />
          <span>Medical Horizon · مراجعة عملي الجراحة</span>
        </div>
      </section>
    </main>
  )
}
